import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { subscribeEmail } from "@/lib/api";
import WorkflowVisual from "@/components/landing/WorkflowVisual";

export const Hero = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeEmail(email, "hero");
      setDone(true);
      setEmail("");
      toast.success("You're in. Check your inbox for the AI Workflow Guide.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 sm:pt-40 pb-12 overflow-hidden bg-white grain-bg"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
        <div
          data-testid="hero-overline"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0052FF] mb-6 reveal"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#0052FF]" />
          AI Workflow Leverage
        </div>

        <h1
          data-testid="hero-headline"
          className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] text-[#0A0A0A] reveal"
          style={{ animationDelay: "60ms" }}
        >
          Use AI to Save
          <br className="hidden sm:block" />{" "}
          <span className="relative inline-block">
            Hours
            <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#0052FF]/15 -z-0" aria-hidden />
          </span>{" "}
          Every Week
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-[#525252] leading-relaxed reveal"
          style={{ animationDelay: "120ms" }}
        >
          Practical AI workflows, tools, and automation systems for operators,
          consultants, agencies, and small businesses.
        </p>

        <form
          onSubmit={handleSubmit}
          data-testid="hero-email-form"
          className="mt-10 max-w-xl mx-auto reveal"
          style={{ animationDelay: "180ms" }}
        >
          <div className="flex flex-col sm:flex-row gap-3 p-2 sm:p-2 bg-white border border-[#E5E5E5] rounded-2xl shadow-sm focus-within:border-[#0052FF] focus-within:ring-2 focus-within:ring-[#0052FF]/20 transition-all">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="hero-email-input"
              aria-label="Email address"
              className="flex-1 bg-transparent px-4 py-3 text-[#0A0A0A] placeholder:text-[#9CA3AF] outline-none text-base"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="hero-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-70 text-white font-semibold py-3 px-5 rounded-xl transition-colors shadow-sm shadow-[#0052FF]/30 focus:outline-none focus:ring-2 focus:ring-[#0052FF] focus:ring-offset-2"
            >
              {loading ? "Sending…" : "Get the Free AI Workflow Guide"}
              {!loading && <ArrowRight size={16} />}
            </button>
          </div>
          {done && (
            <div
              data-testid="hero-success"
              className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[#0A0A0A]"
            >
              <CheckCircle2 size={16} className="text-[#0052FF]" />
              You&apos;re on the list. The guide is on its way.
            </div>
          )}
          <p className="mt-3 text-xs text-[#525252]">
            No spam. One practical AI workflow every week.
          </p>
        </form>

        <a
          href="#social"
          data-testid="hero-secondary-cta"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#0052FF] transition-colors"
        >
          Follow OperatorAI →
        </a>

        <p
          data-testid="hero-social-proof"
          className="mt-10 text-xs uppercase tracking-[0.18em] font-semibold text-[#525252]"
        >
          Trusted by operators, consultants, and AI-driven professionals.
        </p>
      </div>

      {/* Abstract workflow visual */}
      <div
        data-testid="hero-mockup"
        className="relative mt-12 sm:mt-16 max-w-6xl mx-auto px-6 sm:px-8"
      >
        <WorkflowVisual />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
      </div>
    </section>
  );
};

export default Hero;
